import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Grid } from '@mui/material';
import Chip from '@components/common/chip/Chip';
import Companies from '@static/constants/companies';
import { roles } from '@static/constants/roles';
import { IUserModel } from '@src/types/user';
import UserFilters from '../Common/UserFilters';
import UserForm from '../Common/UserForm';
import UserList from '../Common/UserList';
import {
  createOrUpdateUser,
  getUserByGuid,
  getUsersByFilter,
} from '@context/user';
import { CountryList, Rol } from '@src/types/common';
import { SettingsTab } from '@static/constants/settings-tabs';

export const StatusChip = (eventStatus: boolean): JSX.Element => {
  if (eventStatus) {
    return <Chip label="Activo" color="green" />;
  } else {
    return <Chip label="Inactivo" color="red" />;
  }
};

interface Props {
  scrollTrigger: () => void;
  onDeleteUser: (guid: string) => void;
}

const ProvidersTab = (props: Props): JSX.Element => {
  const defaultUserData: IUserModel = {
    name: '',
    email: '',
    rolId: 0,
    companyId: 0,
    guidID: '',
    active: true,
    isMultiCountry: false,
  };

  const [listUser, setListUser] = useState<IUserModel[]>([]);
  const [currentUser, setCurrentUser] = useState<IUserModel>(defaultUserData);
  const [cantRegisters, setCantRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFetch, setActiveFetch] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [labelSubmit, setLabelSubmit] = useState('Crear');
  const [showForm, setShowForm] = useState(false);

  /* User info */
  const [isNewUser, setIsNewUser] = useState(false);
  const [companyUser, setCompanyUser] = useState<number | null>(null);
  const [roleUser, setRoleUser] = useState<number | null>(null);
  const [activeUser, setActiveUser] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const onChangeSearchFilter = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setCurrentPage(1);
    setSearchFilter(event.target.value);
  };

  const onChangeCountryFilter = (event: any) => {
    setCurrentPage(1);
    setCountryFilter(event.target.value);
  };

  const onSearchUserCountry = useCallback(() => {
    const filters: any = [];

    if (searchFilter.length > 0) {
      filters.push({
        name: 'filter',
        value: searchFilter,
      });
    }

    filters.push({
      name: 'countryCode',
      value: countryFilter,
    });

    filters.push({
      name: 'page',
      value: currentPage,
    });
    filters.push({
      name: 'results',
      value: 20,
    });

    (async (): Promise<void> => {
      const resultUsers = await getUsersByFilter('PROVIDER', filters);
      if (resultUsers) {
        setListUser(resultUsers);
        setCantRegisters(resultUsers[0]?.currentCount ?? 0);
      }
    })();
  }, [countryFilter, currentPage, searchFilter]);

  const onShowForm = useCallback(
    (show: boolean, isNew: boolean) => {
      if (isNew) {
        setCurrentUser(defaultUserData);
        setChangePassword(true);
      }

      setIsNewUser(isNew);
      setLabelSubmit(isNew ? 'Crear' : 'Actualizar');
      setShowForm(show);
    },
    [defaultUserData],
  );

  const onGetUser = useCallback(
    (guid: string) => {
      (async (): Promise<void> => {
        const resultUser = await getUserByGuid(guid);
        if (resultUser) {
          setChangePassword(false);
          setCompanyUser(resultUser.companyId);
          setRoleUser(resultUser.rolId ?? null);
          setActiveUser(resultUser.active ?? false);
          setCurrentUser(resultUser);
          onShowForm(true, false);
        }
      })();
    },
    [onShowForm],
  );

  const onChangePage = (_: any, page: number) => {
    const newPage = page === 0 ? 1 : page + 1;
    setCurrentPage(newPage);
    setActiveFetch(true);
  };

  const getLisRoles = (): any[] => {
    return roles.filter((r: any) => [Rol.INSPECTOR].includes(r.id));
  };

  useEffect(() => {
    if (activeFetch) {
      setActiveFetch(false);
      onSearchUserCountry();
    }
  }, [activeFetch, onSearchUserCountry]);

  return (
    <div className="settingsForm">
      {!showForm && (
        <UserFilters
          useCountries={true}
          searchFilter={searchFilter}
          companyFilter={0}
          countryFilter={countryFilter}
          currentCompanies={[]}
          currentCountries={CountryList}
          onEnableForm={onShowForm}
          onChangeCompanyFilter={() => {
            return;
          }}
          onChangeCountryFilter={onChangeCountryFilter}
          onChangeSearchFilter={onChangeSearchFilter}
          onTriggerSearch={onSearchUserCountry}
        />
      )}

      {showForm && (
        <UserForm
          labelForSubmit={labelSubmit}
          isNewUser={isNewUser}
          currentUser={currentUser}
          currentUserCompany={companyUser}
          currentUserRole={roleUser}
          currentUserActive={activeUser}
          listRoles={getLisRoles()}
          listCompanies={Companies().filter(
            (f: any) =>
              String(f.name).toUpperCase().indexOf('CONNECT') > -1 &&
              String(f.name).toUpperCase().indexOf('INSURANCE') === -1,
          )}
          listRunnerOwners={[]}
          enableChangePassword={changePassword}
          onActiveFetch={(value) => {
            setActiveFetch(value);
          }}
          onShowForm={(value) => {
            setShowForm(value);
          }}
          onCreateOrUpdate={createOrUpdateUser}
          currentTab={SettingsTab.PROVIDERS}
        />
      )}
      <Grid container className="settingsForm__row" spacing={2}>
        <UserList
          cantRegisters={cantRegisters}
          currentPage={currentPage}
          listUsers={listUser}
          disabledModify={showForm}
          disabledDelete={showForm}
          onChangePage={onChangePage}
          onModifyUser={onGetUser}
          onDeleteUser={props.onDeleteUser}
          hasCustomLevel={true}
          typeCustomLevel={'DRIVERS'}
          disabledCustomLevel={showForm}
          scrollTrigger={props.scrollTrigger}
        />
      </Grid>
    </div>
  );
};

export default ProvidersTab;
